/**
 * '@palbase/web/next/proxy' — proactive cookie-session refresh in the Next.js
 * proxy (the file convention that replaced `middleware` in Next 16; this
 * package's peer range is `next >= 16`).
 *
 * REQUIRED — not optional — for session-bearing RSC apps. Server Components
 * cannot write cookies, so pbServer's reactive refresh cannot PERSIST a
 * rotated session there: every RSC render would re-refresh from the SAME
 * cookie refresh token, and once two of those land more than 30s apart
 * (outside palauth's rotation grace) the reuse detector REVOKES THE WHOLE
 * TOKEN FAMILY — the user is force-logged-out everywhere. The proxy refreshes
 * the cookie BEFORE the RSC tree renders, so pbServer hydrates a still-valid
 * session and never burns the token itself.
 *
 * BUNDLE RULE — why this is a SEPARATE entry taking an EXPLICIT config
 * instead of reading the process-global one like pbServer does:
 *
 * Next compiles the proxy into its OWN bundle, so the generated palbe.gen.ts
 * imported by app/layout.tsx does not reach it. Importing palbe.gen.ts from
 * proxy.ts to fix that would drag `__configure` → buildRuntime →
 * messaging/wasm/loader.ts into that graph: measured on a real app, 2.4 MB
 * where this entry ships 168 KB. (Under the pre-16 `middleware` convention it
 * was worse than size — that bundle was Edge, and webpack failed the build
 * outright with "Dynamic Code Evaluation ... not allowed in Edge Runtime".
 * `proxy` runs on nodejs, which is not configurable, so the hard error is
 * gone and the weight is not.)
 *
 * So the proxy never touches the runtime: it takes the two published identity
 * values it actually needs and speaks the one auth wire directly. Nothing on
 * this module's import graph may reach ../runtime.js —
 * __tests__/next-proxy-bundle.test.ts locks that against the real build
 * output.
 *
 * WHERE THE CONFIG COMES FROM — and why an explicit argument is not a chore the
 * application has to do. `palbe-gen` writes `palbase/config.ts`, a one-line
 * barrel over an IMPORT-FREE leaf beside the generated client, and rewrites
 * both on every run from PALBASE_ENV. So the proxy follows the same switch the
 * data client follows, while importing a module that has no graph to drag. That
 * is what keeps the rule above intact WITHOUT relying on tree-shaking: the leaf
 * reaches nothing, so there is nothing to shake.
 *
 * Import `palbase/config`, never `palbase/client`. The client barrel re-exports
 * palbe.gen.ts, which calls __configure at import time — that is the 2.4 MB
 * path. The bundle lock measures this chain (app proxy -> generated leaf -> this
 * entry), so pointing the import at the client barrel turns it red.
 *
 * 'next/server' is imported only lazily (the type-only NextRequest/
 * NextResponse imports erase at build time).
 *
 * ```ts
 * // proxy.ts — written by `palbase link`, carries no address and no key
 * import { palbeProxy } from '@palbase/web/next/proxy';
 * import { environmentConfig } from './palbase/config';
 * import type { NextRequest } from 'next/server';
 *
 * export function proxy(request: NextRequest) {
 *   return palbeProxy(request, environmentConfig);
 * }
 *
 * // `config` MUST be declared here, in this file: Next reads it off THIS
 * // file's AST (export const + literal initializer). A re-exported or
 * // imported config is invisible and silently degrades to a catch-all.
 * export const config = { matcher: ['/((?!_next/static|_next/image|favicon.ico).*)'] };
 * ```
 */

import { HttpClient, type HttpClientOptions } from '@palbase/core';
import type { NextRequest, NextResponse } from 'next/server';
import { environmentRefFromPublishableApiKey } from '../api-key.js';
import { BackendError } from '../errors.js';
import {
  clearedSessionCookieNames,
  decodeSessionCookies,
  encodeSessionCookiesDecoded,
  SESSION_COOKIE_ATTRS,
  type StoredSession,
} from './cookie-codec.js';
import { importNextServer } from './shared.js';

const DEFAULT_REFRESH_MARGIN_MS = 60_000;

/**
 * The published identity palbeProxy needs — the same two values
 * `palbase web link` bakes into palbe.gen.ts. Both are publishable (the
 * anon key already ships in every browser bundle); a service_role key is
 * REJECTED, it has no business in a request-path bundle.
 *
 * A `PalbeConfig` is structurally assignable, so apps that already hold one
 * can pass it straight through.
 */
export interface PalbeProxyConfig {
  /** Environment URL, e.g. `https://abc12345m.palbase.studio`. */
  url: string;
  /** Publishable (anon) API key: `pb_<environmentRef>_c<random>`. */
  apiKey: string;
  /** Same auth encryption settings as the generated web configuration. */
  sealed?: HttpClientOptions['sealed'];
}

export interface PalbeProxyOptions {
  /** Refresh when the access token expires within this window. Default 60s. */
  refreshMarginMs?: number;
  /**
   * Pre-built response to decorate (e.g. from your own chained proxy logic) instead of
   * building `NextResponse.next({ request })`. Pass-through cases return it
   * UNMODIFIED. Caveat: a pre-built response snapshotted the request headers
   * at ITS creation, so a rotated cookie reaches the browser (Set-Cookie)
   * but not the downstream RSC render of THIS request — prefer omitting this
   * and building your response after palbeProxy when possible.
   */
  response?: NextResponse;
}

type RefreshOutcome =
  | { kind: 'rotated'; session: StoredSession }
  | { kind: 'terminal' } // 400/401/403 — refresh token dead, sign out
  | { kind: 'transient' }; // network / 5xx / malformed — keep the session

/**
 * Per-process single-flight: concurrent proxy invocations holding the
 * SAME refresh token (parallel tabs / prefetches) share one refresh call —
 * a per-token thundering herd would race palauth rotation. Multi-pod
 * concurrency is covered by palauth's 30s rotation grace (concurrent
 * refreshes of one token all succeed); per-process dedup is all that's
 * needed here (Studio precedent — don't over-engineer).
 */
const inflightRefreshes = new Map<string, Promise<RefreshOutcome>>();

function refreshSingleFlight(
  config: PalbeProxyConfig,
  refreshToken: string,
): Promise<RefreshOutcome> {
  const existing = inflightRefreshes.get(refreshToken);
  if (existing) return existing;
  const pending = refresh(config, refreshToken).finally(() => {
    inflightRefreshes.delete(refreshToken);
  });
  inflightRefreshes.set(refreshToken, pending);
  return pending;
}

/** Shared sealed transport, without importing the browser runtime. */
async function refresh(config: PalbeProxyConfig, refreshToken: string): Promise<RefreshOutcome> {
  try {
    const http = new HttpClient(config.apiKey, { url: config.url, sealed: config.sealed });
    const res = await http.request<unknown>('POST', '/auth/token/refresh', {
      body: { refresh_token: refreshToken },
      retry: false,
    });
    if (res.status === 400 || res.status === 401 || res.status === 403) {
      return { kind: 'terminal' };
    }
    if (res.error) return { kind: 'transient' };
    const raw: unknown = res.data;
    if (typeof raw === 'object' && raw !== null) {
      const obj = raw as Record<string, unknown>;
      if (
        typeof obj.access_token === 'string' &&
        typeof obj.refresh_token === 'string' &&
        typeof obj.expires_in === 'number'
      ) {
        return {
          kind: 'rotated',
          session: {
            accessToken: obj.access_token,
            refreshToken: obj.refresh_token,
            expiresAt: Date.now() + obj.expires_in * 1000,
          },
        };
      }
    }
    return { kind: 'transient' }; // malformed 200 — never destroy a session over it
  } catch {
    return { kind: 'transient' }; // network failure
  }
}

/**
 * Keep the session cookie fresh for the request. No session / still-fresh
 * session → untouched pass-through (no cache-control changes). Stale or
 * expired → single-flight refresh, then the rotated (or cleared, on a dead
 * token) cookies are written onto BOTH the request (for the downstream RSC
 * render via `NextResponse.next({ request })`) and the response (for the
 * browser), with `Cache-Control: private, no-store` on touched responses.
 *
 * Cookie writes go through Next's cookie APIs with the DECODED codec values:
 * Next percent-encodes on serialization, so the wire bytes come out exactly
 * as the shared codec's (see encodeSessionCookiesDecoded). NextRequest's
 * cookie store is NOT a CookieStoreLike (its set is the 2-arg/RequestCookie
 * form), hence the direct codec calls here instead of the pbServer adapter.
 */
export async function palbeProxy(
  request: NextRequest,
  config: PalbeProxyConfig,
  opts?: PalbeProxyOptions,
): Promise<NextResponse> {
  // Fail LOUDLY on a bad key. A silently empty ref would name the cookie
  // `palbe-session-` — a name the browser SDK never writes — so every
  // request would look session-less and the proxy would degrade to a
  // no-op, which is exactly the token-family revocation this exists to stop.
  const ref = environmentRefFromPublishableApiKey(config.apiKey);
  if (ref === '') {
    throw new BackendError('validation', {
      code: 'validation_error',
      message:
        'palbeProxy() needs the publishable API key from palbe.gen.ts (pb_<environmentRef>_c…). Re-run `palbase web link` and copy the generated values.',
    });
  }
  const { NextResponse } = await importNextServer('palbeProxy');
  const passThrough = (): NextResponse => opts?.response ?? NextResponse.next({ request });

  const session = decodeSessionCookies((name) => request.cookies.get(name)?.value, ref);
  if (!session) return passThrough();
  if (session.expiresAt - Date.now() > (opts?.refreshMarginMs ?? DEFAULT_REFRESH_MARGIN_MS)) {
    return passThrough();
  }

  const outcome = await refreshSingleFlight(config, session.refreshToken);
  if (outcome.kind === 'transient') return passThrough(); // keep the session over blips

  // Session-cookie names in the incoming jar — the browser's state mirror.
  const staleNames = clearedSessionCookieNames(ref, (name) => request.cookies.has(name));

  if (outcome.kind === 'terminal') {
    for (const name of staleNames) request.cookies.delete(name);
    const response = opts?.response ?? NextResponse.next({ request });
    for (const name of staleNames) {
      response.cookies.set(name, '', { ...SESSION_COOKIE_ATTRS, maxAge: 0 });
    }
    response.headers.set('cache-control', 'private, no-store');
    return response;
  }

  const write = encodeSessionCookiesDecoded(ref, outcome.session);
  for (const name of staleNames) request.cookies.delete(name);
  for (const { name, value } of write.set) request.cookies.set(name, value);
  for (const name of write.clear) request.cookies.delete(name);
  // Build AFTER the request-cookie mutation: NextResponse.next({ request })
  // snapshots the request headers for the downstream RSC render.
  const response = opts?.response ?? NextResponse.next({ request });
  const written = new Set(write.set.map((c) => c.name));
  for (const { name, value } of write.set) {
    response.cookies.set(name, value, SESSION_COOKIE_ATTRS);
  }
  // Browser-side cleanup: stale names not part of the new run (a shrunken /
  // grown chunk layout) + the codec's one-past-the-end overflow guard.
  for (const name of new Set([...write.clear, ...staleNames])) {
    if (!written.has(name)) {
      response.cookies.set(name, '', { ...SESSION_COOKIE_ATTRS, maxAge: 0 });
    }
  }
  response.headers.set('cache-control', 'private, no-store');
  return response;
}
